import { createTheme, Theme } from '@mui/material/styles';
import { AppName } from '@app/utils/appNames';
import {
    blue,
    orange,
    pink,
    deepOrange,
    grey,
    indigo,
    red,
} from '@mui/material/colors';

declare module '@mui/material/styles' {
    interface Theme {
        custom: {
            appBarBackground: string;
            menuItemColour: string;
            mobileMenuItemColour: string;
            mobileMenuIconColour: string;
            loginButtonColour: string;
            userNameColour: string;
            userMenuItemColour: string;
            labelColour: string;
            exampleColour?: string;
            priceHeadingColour?: string;
            threadHeadingColour?: string;
            logo: string;
            altLogo: string;
            backgroundImage: string;
            backgroundColor?: string;
            backgroundRepeat?: string;
            backgroundSize?: string;
            backgroundEdgeFade?: boolean;
            backgroundEdgeFadeSize?: string;
            logoText?: string | undefined;
            logoTextSize?: string | undefined;
            logoTextColor?: string | undefined;
            logoTextFont?: string | undefined;
            displayBarLogoOnHome: boolean;
        };
    }

    interface TypeText {
        description?: string;
    }

    interface ThemeOptions {
        custom?: {
            appBarBackground?: string;
            menuItemColour?: string;
            mobileMenuItemColour?: string;
            mobileMenuIconColour?: string;
            loginButtonColour?: string;
            userNameColour?: string;
            userMenuItemColour?: string;
            labelColour?: string;
            exampleColour?: string;
            priceHeadingColour?: string;
            threadHeadingColour?: string;
            logo?: string;
            altLogo?: string;
            backgroundImage?: string;
            backgroundColor?: string;
            backgroundRepeat?: string;
            backgroundSize?: string;
            backgroundEdgeFade?: boolean;
            backgroundEdgeFadeSize?: string;
            logoText?: string | undefined;
            logoTextSize?: string | undefined;
            logoTextColor?: string | undefined;
            logoTextFont?: string | undefined;
            displayBarLogoOnHome?: boolean;
        };
    }
}

// ICE AI THEME
export const iceAiTheme = createTheme({
    palette: {
        primary: {
            main: blue[800],
        },
        secondary: {
            main: grey[800],
        },
        text: {
            primary: blue[900],
            secondary: grey[600],
        },
    },
    custom: {
        appBarBackground: '#24435c',
        menuItemColour: '#f5f5f5',
        mobileMenuItemColour: '#202020',
        mobileMenuIconColour: 'cyan',
        loginButtonColour: 'lightblue',
        userNameColour: 'lightblue',
        userMenuItemColour: 'blue',
        labelColour: pink[500],
        logo: '/images/ice-ai-logo.png',
        altLogo: "ice-ai",
        backgroundImage: '/images/ice-ai-background.png',
        logoText: undefined,
        logoTextSize: undefined,
        logoTextColor: undefined,
        logoTextFont: undefined,
        displayBarLogoOnHome: false,
    },
});

// AUTO ADS THEME
export const autoAdsTheme = createTheme({
    palette: {
        primary: {
            main: deepOrange[900],
        },
        secondary: {
            main: orange[700],
        },
        text: {
            primary: grey[900],
            secondary: orange[900],
        },
    },
    typography: {
        h3: {
            color: deepOrange[600],
        },
        subtitle2: {
            color: deepOrange[300],
        },
    },
    components: {
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    color: red[900],
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    backgroundColor: 'rgba(255, 255, 255, 0.6)',
                },
            },
        },
        MuiFilledInput: {
            styleOverrides: {
                root: {
                    backgroundColor: 'rgba(255, 255, 255, 0.6)',
                },
            },
        },
    },
    custom: {
        appBarBackground: '#33363A',
        menuItemColour: deepOrange[600],
        mobileMenuItemColour: deepOrange[600],
        mobileMenuIconColour: deepOrange[800],
        loginButtonColour: deepOrange[800],
        userNameColour: deepOrange[800],
        userMenuItemColour: deepOrange[900],
        labelColour: red[900],
        logo: '/images/auto-ads-logo.png',
        altLogo: "auto-ads",
        backgroundImage: '/images/auto-ads-background.png',
        logoText: undefined,
        logoTextSize: undefined,
        logoTextColor: undefined,
        logoTextFont: undefined,
        displayBarLogoOnHome: true,
    },
});

// ANIA METHOD THEME
export const aniaMethodTheme = createTheme({
    palette: {
        primary: {
            main: indigo[300],
        },
        secondary: {
            main: indigo["A100"],
        },
        text: {
            primary: indigo[600],
            secondary: grey[600],
            description: indigo[300],
        },
    },
    custom: {
        appBarBackground: '#e6e6e6',
        menuItemColour: 'slateblue',
        mobileMenuIconColour: 'purple',
        mobileMenuItemColour: 'slateblue',
        loginButtonColour: '#b300b3',
        userNameColour: '#b300b3',
        userMenuItemColour: '#b300b3',
        labelColour: indigo[600],
        logo: '/images/ania-method-logo.png',
        altLogo: "The Ania Method",
        backgroundImage: '/images/anna-trainer-background.png',
        logoText: undefined,
        logoTextSize: undefined,
        logoTextColor: undefined,
        logoTextFont: undefined,
        displayBarLogoOnHome: true,
    },
});

// CLASSICS AI THEME
export const classicsAiTheme = createTheme({
    palette: {
        primary: {
            main: '#0b4624',
        },
        secondary: {
            main: '#5d0b0a',
        },
        text: {
            primary: '#0b4624',
            secondary: '#5d0b0a',
        },
        background: {
            default: '#ffffff',
        },
    },
    custom: {
        appBarBackground: '#efe6d4',
        menuItemColour: '#0b4624',
        mobileMenuItemColour: '#0b4624',
        mobileMenuIconColour: '#0b4624',
        loginButtonColour: '#5d0b0a',
        userNameColour: '#5d0b0a',
        userMenuItemColour: '#0b4624',
        labelColour: 'brown',
        exampleColour: '#2C1B18',
        priceHeadingColour: '#3a2414',
        threadHeadingColour: '#3a2414',
        logo: '/images/classics-ai-logo-1.png',
        altLogo: 'Classics AI',
        backgroundImage: '/images/classics-ai-background.jpg',
        backgroundColor: '#ffffff',
        backgroundSize: 'cover',
        backgroundEdgeFade: true,
        backgroundEdgeFadeSize: '2.5cm',
        logoText: undefined,
        logoTextSize: undefined,
        logoTextColor: undefined,
        logoTextFont: undefined,
        displayBarLogoOnHome: false,
    },
});

// APP THEMES
export const AppThemes: Record<AppName, Theme> = {
    [AppName.IceAI]: iceAiTheme,
    [AppName.AutoAds]: autoAdsTheme,
    [AppName.AniaMethod]: aniaMethodTheme,
    [AppName.ClassicsAI]: classicsAiTheme,
};
